import React, { PureComponent, ChangeEvent } from 'react';
import { Trans } from 'react-i18next';
import { Button } from '@jg/react-tracking';
import Loading from '@jg/loading';
import Svg from '@jg/svg';
import * as icons from '@jg/icons';
import { ITeamSettingsForm } from './TeamSettingsForm.d';
import AmountInput from '../AmountInput/AmountInput';
import FormError from '../FormError/FormError';

class TeamSettingsForm extends PureComponent<ITeamSettingsForm> {
  onFundraisingTargetChange = (
    event: Pick<ChangeEvent<HTMLInputElement>, 'target'>,
  ) => {
    const { setFieldValue } = this.props;
    setFieldValue('teamFundraisingTarget', event.target.value);
  };

  onTeamMembersTargetChange = (
    event: Pick<ChangeEvent<HTMLInputElement>, 'target'>,
  ) => {
    const { setFieldValue } = this.props;
    setFieldValue('teamMembersTarget', event.target.value);
  };

  onShortNameChange = (
    event: Pick<ChangeEvent<HTMLInputElement>, 'target'>,
  ) => {
    const { setFieldValue } = this.props;
    setFieldValue('shortName', event.target.value);
  };

  onShortNameKeyup = (event: ChangeEvent<any>) => {
    // TODO Why is this called handleOnKeyUp, its input specific
    const {
      handleOnKeyUp,
      errors,
      clearShortNameValidity,
      isShortNameValid,
    } = this.props;
    if (!errors.shortName || errors.shortName.length === 0) {
      handleOnKeyUp(event);
    } else if (isShortNameValid === true || isShortNameValid === false) {
      clearShortNameValidity();
    }
  };

  render() {
    const {
      setTeamMembersTarget,
      handleClick,
      handleSubmit,
      values,
      errors,
      touched,
      isShortNameValid,
      currencyCode,
      handleBlur,
      isInFlight,
      teamStatus,
      shortNameCheckIsInFlight,
      shortNameCheckIsError,
    } = this.props;
    const displayTeamFundraisingTargetErrorMessage = Boolean(
      touched.teamFundraisingTarget && errors.teamFundraisingTarget,
    );
    const displayShortNameErrorMessage = Boolean(
      (values.shortName.length > 2 || touched.shortName) && errors.shortName,
    );
    const inputClass =
      touched.shortName && errors.shortName
        ? 'jg-form-control jg-form-control--error'
        : 'jg-form-control';

    return (
      <form onSubmit={handleSubmit} autoComplete="off">
        <AmountInput
          id="teamFundraisingTarget"
          name="teamFundraisingTarget"
          intlId="content.teamsettings.teamFundraisingTarget"
          value={values.teamFundraisingTarget}
          defaultCurrency={currencyCode} // TODO to change to currency save in the state once API is ready
          onBlur={handleBlur}
          onChange={this.onFundraisingTargetChange}
          hasValidationError={displayTeamFundraisingTargetErrorMessage}
          min={0}
        />
        {displayTeamFundraisingTargetErrorMessage && (
          <FormError message={errors.teamFundraisingTarget} />
        )}
        <p className="jg-text--medium-light jg-space-mtxs jg-space-mbmd">
          <Trans i18nKey="content.teamsettings.changetarget" />
        </p>

        <button
          id="setTeamMembersTargetLink"
          onClick={handleClick}
          className="container--flex jg-btn--ghost jg-bdn jg-space-pn"
          type="button"
        >
          <span className="jg-text--medium-reg jg-text--link">
            <Trans i18nKey="content.teamsettings.setTeamMembersTarget" />
          </span>
          <Svg
            id="setTeamMembersTargetIcon"
            markup={
              setTeamMembersTarget ? icons.chevronUpIcon : icons.chevronDownIcon
            }
            className="jg-icon jg-icon--medium-small jg-fill--denim jg-display-ib jg-space-mlxs"
          />
        </button>
        {setTeamMembersTarget && (
          <AmountInput
            id="teamMembersTarget"
            name="teamMembersTarget"
            intlId="content.teamsettings.teamMembersTarget"
            value={values.teamMembersTarget}
            defaultCurrency={currencyCode}
            onBlur={handleBlur}
            onChange={this.onTeamMembersTargetChange}
          />
        )}
        {teamStatus !== 'active' && (
          <>
            <label
              className="jg-h3 jg-space-mtml jg-space-mbxs jg-display-b"
              htmlFor="shortName"
            >
              <Trans i18nKey="content.teamsettings.shortname" />
            </label>
            <p className="jg-text--large jg-space-mbxs">
              <Trans i18nKey="content.teamsettings.shortname.subtext" />
            </p>
            <input
              id="shortName"
              className={inputClass}
              type="text"
              name="shortName"
              value={values.shortName}
              onChange={this.onShortNameChange}
              onKeyUp={this.onShortNameKeyup}
              aria-invalid={displayShortNameErrorMessage || !isShortNameValid}
              aria-describedby="eg-team-name"
            />

            <span
              id="eg-team-name"
              className=" jg-text--xsmall-light jg-space-mtxs jg-space-mbml"
            >
              <Trans i18nKey="content.teamsettings.shortname.egteamname" />
            </span>

            {displayShortNameErrorMessage && (
              <FormError message={errors.shortName} />
            )}
            {shortNameCheckIsError && (
              <FormError
                message={<Trans i18nKey="global.shortname.errorMessage" />}
              />
            )}
            {!errors.shortName && (
              <>
                {isShortNameValid && (
                  <span
                    className="jg-form-input-success jg-display-flex jg-space-ptxs"
                    role="alert"
                  >
                    <Svg
                      markup={icons.selectIcon}
                      className="jg-icon jg-fill--mountain-meadow"
                    />
                    <div className="success-color jg-space-mlxs">
                      <Trans i18nKey="content.teamsettings.shortname.success.available" />
                    </div>
                  </span>
                )}
                {shortNameCheckIsInFlight && (
                  <div className="jg-space-ptxs jg-color--empress">
                    <Trans i18nKey="global.shortname.checkingShortnameAvailability" />
                  </div>
                )}
                {isShortNameValid === false && (
                  <span
                    className="jg-form-error-copy jg-display-flex"
                    role="alert"
                  >
                    <div className="error-color">
                      <Trans i18nKey="content.teamsettings.shortname.error.taken" />
                    </div>
                  </span>
                )}
              </>
            )}
          </>
        )}
        <div className="jg-tc-button-container">
          <Button
            disabled={isInFlight}
            type="submit"
            id="jg-tc-create-team-continue-button"
            className="jg-btn jg-space-mbxl jg-space-mtml"
            trackingData={{
              action_name: 'click',
              subtype: 'button',
              event: 'click',
              event_value: 'create your team',
              page_section: 'page creation > team settings',
            }}
          >
            {isInFlight ? (
              <Loading size="small" colour="purple" />
            ) : (
              <Trans i18nKey="global.button.createyourteam" />
            )}
          </Button>
        </div>
      </form>
    );
  }
}

export default TeamSettingsForm;

// TODO Check shortname and spaces???
